import React, { useEffect, useState } from "react";
import CustomModal from "../../../components/generic/custommodal/CustomModal";
import SecondaryButton from "../../../components/generic/buttons/SecondaryButton";
import PrimaryButton from "../../../components/generic/buttons/PrimaryButton";
import AddEditWorkforceFormComponent from "./AddEditWorkforceFormComponent";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import { executeIntent } from "../../utils/IntentExecutor";
import IntentType from "../../../intentexecutor/utils/IntentType";
import { notification } from "antd";
import useGetWorkforceDetailsByIdentifier from "../../generic/hooks/useGetWorkforceDetailsByIdentifier";
import { Subject } from "rxjs";
import { EventType } from "../../../service/network/utils/EventType";
import { useReadMemory } from "../../generic/hooks/useReadMemory";
import MemoryType from "../../../service/memory/enum/MemoryType";
import { strings } from "../../utils/strings";
import { statusCode } from "../../utils/statuscodes";
import { convertDateToEODTimestamp } from "../../../utils/dateutils";
import { useRecoilValue } from "recoil";
import { officeLabel } from "../../../components/recoil/officeLabelRecoil";
import ConfigurationConstants from "../../utils/configurationconstants";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";
import { useWriteMemory } from "../../generic/hooks/useWriteMemory";

export const workforceDataUpdateResponse = new Subject();

const AddEditWorkforceModal = ({
  openAddWorkforceModal,
  setOpenAddWorkforceModal,
  editVolunteer,
  userIdentifier,
  showAdditionalFields,
  organizationId,
  disableEmail,
}) => {
  const orgIdentifier = useGetOrgIdentifier();
  const configuration = useRecoilValue(currentConfiguration);
  const organizationIdentifier = organizationId || orgIdentifier;
  const loggedInUser = useReadMemory(MemoryType.USER);
  const setMemory = useWriteMemory();
  const [workforceData, setWorkforceData] = useState({
    givenName: "",
    familyName: "",
    email: "",
  });
  const [metadata, setMetadata] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emergencyContact, setEmergencyContact] = useState({});
  const [isTeamRequired, setIsTeamRequired] = useState(true);
  const label = useRecoilValue(officeLabel);

  const [workforceDetails, workforceDetailsLoading] =
    useGetWorkforceDetailsByIdentifier(userIdentifier);

  const toggleAddWorkforceModal = () => {
    setOpenAddWorkforceModal(!openAddWorkforceModal);
  };

  const handleResponse = (response, successMessage, errorMessage) => {
    if (response.identifier) {
      notification.success({
        message: successMessage,
        placement: "bottomRight",
      });
      return true;
    } else {
      notification.error({
        message: errorMessage,
        placement: "bottomRight",
      });
      return false;
    }
  };

  const handleError = () => {
    notification.error({
      message: "Something went wrong!",
      placement: "bottomRight",
    });
  };

  const trimIfString = (value) =>
    typeof value === "string" ? value.trim() : value;

  const syncLoggedInUserToStorage = (updatedUser) => {
    if (userIdentifier !== loggedInUser?.userIdentifier) {
      return;
    }

    setMemory(MemoryType.USER, updatedUser);
  };

  const validate = (values) => {
    const skipFields = !showAdditionalFields ? ["completeAddress"] : [];

    const errors = {};
    const emailRegex = /^[\w\.-]+@[a-zA-Z\d\.-]+\.[a-zA-Z]{2,}$/;

    const requiredFields = [
      { field: "givenName", message: strings.FIRST_NAME_ERROR_MESSAGE },
      { field: "familyName", message: strings.LAST_NAME_ERROR_MESSAGE },
      { field: "jobTitle", message: strings.JOB_TITLE_ERROR_MESSAGE },
      { field: "completeAddress", message: strings.ADDRESS_ERROR_MESSAGE },
      { field: "email", message: strings.EMAIL_ERROR_MESSAGE },
      ...(isTeamRequired
        ? [{ field: "teams", message: `${label} is required` }]
        : []),
    ];

    requiredFields
      .filter(({ field }) => !skipFields.includes(field))
      .forEach(({ field, message }) => {
        if (field === "teams") {
          const teams = values?.[field];
          if (!Array.isArray(teams) || teams.length === 0) {
            errors[field] = message;
          }
        } else if (!trimIfString(values?.[field])) {
          errors[field] = message;
        }
      });

    const trimmedEmail = trimIfString(values?.email);
    if (trimmedEmail && !emailRegex.test(trimmedEmail)) {
      errors.email = strings.VALID_EMAIL_ERROR_MESSAGE;
    }

    return errors;
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setFormErrors(validate(workforceData));
    setIsSubmit(true);
  };

  const addVolunteer = async () => {
    setIsLoading(true);
    let response;
    const payload = {
      data: {
        pronoun: trimIfString(workforceData?.pronoun),
        givenName: trimIfString(workforceData?.givenName),
        familyName: trimIfString(workforceData?.familyName),
        email: trimIfString(workforceData?.email),
        telephone: trimIfString(workforceData?.telephone),
        organizationIdentifier: organizationIdentifier,
        jobTitle: trimIfString(workforceData?.jobTitle),
        gender: workforceData?.gender,
        birthDate: workforceData?.birthDate && convertDateToEODTimestamp(workforceData?.birthDate),
        status: "Invited",
        workforceStatus: "Active",
        sendInvitation: true,
        creatorIdentifier: loggedInUser?.userIdentifier,
        teams: workforceData?.teams,
      },
    };
    try {
      response = await executeIntent(IntentType.ADD_WORKFORCE, payload);

      if (response?.code === statusCode.EMAIL_ALREADY_EXISTS) {
        setFormErrors({ email: strings.EMAIL_ALREADY_EXISTS });
      } else if (response?.code === statusCode.TELEPHONE_ALREADY_EXISTS) {
        setFormErrors({ telephone: strings.TELEPHONE_ALREADY_EXISTS });
      } else {
        handleResponse(
          response,
          strings.INVITE_WORKFORCE_SUCCESS,
          strings.INVITE_WORKFORCE_ERROR
        );
        workforceDataUpdateResponse.next({
          type: EventType.WORKFORCE_INVITED,
        });
      }
    } catch (err) {
      handleError();
    } finally {
      setIsLoading(false);
      if (
        response?.code !== statusCode.EMAIL_ALREADY_EXISTS &&
        response?.code !== statusCode.TELEPHONE_ALREADY_EXISTS
      ) {
        toggleAddWorkforceModal();
      }
    }
  };

  const updateVolunteer = async () => {
    setIsLoading(true);
    let response;
    const payload = {
      data: {
        userIdentifier: userIdentifier,
        pronoun: trimIfString(workforceData?.pronoun),
        givenName: trimIfString(workforceData?.givenName),
        familyName: trimIfString(workforceData?.familyName),
        email: trimIfString(workforceData?.email),
        telephone: trimIfString(workforceData?.telephone),
        organizationIdentifier: organizationIdentifier,
        jobTitle: trimIfString(workforceData?.jobTitle),
        gender: workforceData?.gender,
        creatorIdentifier: loggedInUser?.userIdentifier,
        birthDate:
          workforceData?.birthDate &&
          convertDateToEODTimestamp(workforceData?.birthDate),
        anniversary:
          workforceData?.anniversary &&
          convertDateToEODTimestamp(workforceData?.anniversary),
        teams: workforceData?.teams,
        latitude: trimIfString(workforceData?.latitude),
        longitude: trimIfString(workforceData?.longitude),
        comments: trimIfString(workforceData?.comments),
        employeeCode: trimIfString(workforceData?.employeeCode),
        address: {
          streetAddress: trimIfString(workforceData?.streetAddress),
          addressLocality: trimIfString(workforceData?.addressLocality),
          addressRegion: trimIfString(workforceData?.addressRegion),
          postalCode: trimIfString(workforceData?.postalCode),
          completeAddress: trimIfString(workforceData?.completeAddress),
          addressCountry: trimIfString(workforceData?.addressCountry),
        },
        metadata: JSON.stringify({ ...metadata, emergencyContact }),
        workforceStatus: workforceData?.workforceStatus,
        isActive: workforceData?.workforceStatus
          ? workforceData?.workforceStatus === "Active"
            ? true
            : false
          : workforceData?.isActive || false,
        isPPC: workforceData?.isPPC || false,
        isFPC: workforceData?.isFPC || false,
      },
    };
    try {
      response = await executeIntent(IntentType.UPDATE_WORKFORCE, payload);

      if (response?.code === statusCode.EMAIL_ALREADY_EXISTS) {
        setFormErrors({ email: strings.EMAIL_ALREADY_EXISTS });
      } else if (response?.code === statusCode.TELEPHONE_ALREADY_EXISTS) {
        setFormErrors({ telephone: strings.TELEPHONE_ALREADY_EXISTS });
      } else {
        handleResponse(
          response,
          strings.UPDATE_WORKFORCE_SUCCESS,
          strings.UPDATE_WORKFORCE_ERROR
        );
        syncLoggedInUserToStorage(response);
        workforceDataUpdateResponse.next({
          type: EventType.WORKFORCE_DATA_UPDATED,
        });
      }
    } catch (err) {
      handleError();
    } finally {
      setIsLoading(false);
      if (
        response?.code !== statusCode.EMAIL_ALREADY_EXISTS &&
        response?.code !== statusCode.TELEPHONE_ALREADY_EXISTS
      ) {
        toggleAddWorkforceModal();
      }
    }
  };

  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmit) {
      if (editVolunteer) {
        updateVolunteer();
      } else {
        addVolunteer();
      }
    }
  }, [formErrors]);

  useEffect(() => {
    setWorkforceData(workforceDetails);
    const parsedMetadata = workforceDetails?.metadata
      ? JSON.parse(workforceDetails?.metadata)
      : {};
    setMetadata(parsedMetadata);
    setEmergencyContact(parsedMetadata.emergencyContact);
  }, [workforceDetails]);

  const footerButtonsForAddVolunteer = (
    <>
      <SecondaryButton
        buttonText={"Cancel"}
        handleOnClick={toggleAddWorkforceModal}
      />
      <PrimaryButton
        buttonText={editVolunteer ? "Update" : "Add"}
        handleOnClick={handleSubmitForm}
        loading={isLoading}
      />
    </>
  );

	useEffect(() => {
		const configurationAllowTeamSelect =
			configuration[ConfigurationConstants.FEATUREACCESS_MENUHEADER_TEAMSELECT];
		setIsTeamRequired(configurationAllowTeamSelect?.visibility);
	}, [configuration]);

  return (
    <>
      <CustomModal
        closable={false}
        isModalOpen={openAddWorkforceModal}
        footer={footerButtonsForAddVolunteer}
        closeModal={setOpenAddWorkforceModal}
        title={editVolunteer ? "Edit Team Member" : "Add Team Member"}
      >
        <AddEditWorkforceFormComponent
          workforceData={workforceData}
          setWorkforceData={setWorkforceData}
          metadata={metadata}
          setMetadata={setMetadata}
          workforceDetailsLoading={workforceDetailsLoading}
          formErrors={formErrors}
          showAdditionalFields={showAdditionalFields}
          emergencyContact={emergencyContact}
          setEmergencyContact={setEmergencyContact}
          disableEmail={disableEmail}
          label={label}
          isTeamRequired={isTeamRequired}
        />
      </CustomModal>
    </>
  );
};

export default AddEditWorkforceModal;
